import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search as SearchIcon } from "lucide-react";
import { searchCards, type CardSummary } from "../lib/api";
import { getScryfallCard, imageFor, backImageFor } from "../lib/scryfall";
import CardImage from "../components/CardImage";
import { cn } from "../lib/cn";

/** Debounce a string value so we don't fire a request on every keystroke. */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export default function CardSearch() {
  const [q, setQ] = useState("");
  const debounced = useDebounced(q.trim(), 280);
  const enabled = debounced.length >= 2;

  const { data, isFetching, error } = useQuery({
    queryKey: ["search", debounced],
    queryFn: () => searchCards(debounced, 20),
    enabled,
  });

  const [selected, setSelected] = useState<CardSummary | null>(null);
  // Reset selection if query changes enough.
  useEffect(() => {
    setSelected(null);
  }, [debounced]);

  return (
    <div className="mx-auto max-w-5xl px-6 md:px-10 py-10 space-y-8">
      <header>
        <div className="eyebrow">Search</div>
        <h1 className="mt-2 font-display text-3xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.0]">
          Find any card.
          <br />
          <span className="foil">By intent or image, not just text.</span>
        </h1>
        <p className="text-sm text-fg-muted mt-5 max-w-xl leading-relaxed">
          Hybrid BM25 + dense + Cohere rerank over the Standard-legal card pool.
          Ask for a card by name, oracle text, ability shape, or archetype role.
        </p>
      </header>

      <div className="relative">
        <SearchIcon
          size={15}
          strokeWidth={1.75}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle"
        />
        <input
          className="input pl-9 py-3 text-base"
          placeholder="Type an oracle phrase, card name, type, or archetype…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
        />
      </div>

      {error && (
        <div className="surface p-4 text-sm text-danger">
          {(error as Error).message}
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
        <div className="space-y-2">
          {!enabled && (
            <div className="text-xs text-fg-subtle px-1">
              Type at least 2 characters to search.
            </div>
          )}
          {isFetching &&
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="skeleton h-14 w-full" />
            ))}
          {data?.hits.map((h) => (
            <ResultRow
              key={h.oracle_id}
              hit={h}
              active={selected?.oracle_id === h.oracle_id}
              onClick={() => setSelected(h)}
            />
          ))}
          {data && data.hits.length === 0 && (
            <div className="surface p-6 text-sm text-fg-muted text-center">
              No results.
            </div>
          )}
        </div>

        <CardDetailPanel card={selected} />
      </div>
    </div>
  );
}

function ResultRow({
  hit,
  active,
  onClick,
}: {
  hit: CardSummary;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "surface surface-hover w-full text-left p-3 flex gap-3",
        active && "border-accent/60",
      )}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate">{hit.name}</span>
          {hit.mana_cost && (
            <span className="font-mono text-[11px] text-fg-subtle">
              {hit.mana_cost}
            </span>
          )}
        </div>
        <div className="text-[11px] text-fg-subtle truncate mt-0.5">
          {hit.type_line} · <span className="uppercase tracking-wider">{hit.set_code}</span>
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono text-[11px] text-fg-subtle tabular-nums">
          {hit.score.toFixed(2)}
        </div>
        <div className="text-[10px] text-fg-subtle uppercase tracking-wider mt-0.5">
          {hit.source}
        </div>
      </div>
    </button>
  );
}

function CardDetailPanel({ card }: { card: CardSummary | null }) {
  const { data: sc } = useQuery({
    queryKey: ["scryfall", card?.name],
    queryFn: () => (card ? getScryfallCard(card.name) : Promise.resolve(null)),
    enabled: !!card,
  });
  const img = sc ? imageFor(sc, "normal") : null;
  const backImg = sc ? backImageFor(sc, "normal") : null;
  return useMemo(
    () => (
      <aside className="lg:sticky lg:top-10">
        {!card ? (
          <div className="surface p-6 text-sm text-fg-subtle text-center">
            Select a card to see its full details and Scryfall image.
          </div>
        ) : (
          <div className="surface p-4 space-y-4">
            {img ? (
              <div className="anim-fade-in">
                <CardImage front={img} back={backImg} alt={card.name} />
              </div>
            ) : (
              <div className="skeleton aspect-[488/680] w-full" />
            )}
            <div>
              <div className="text-sm font-semibold">{card.name}</div>
              <div className="text-[11px] text-fg-subtle mt-0.5">
                {card.type_line} · {card.set_code.toUpperCase()} ·{" "}
                <span className="capitalize">{card.rarity}</span>
              </div>
              {card.mana_cost && (
                <div className="mt-2 font-mono text-xs text-fg-muted">
                  {card.mana_cost}
                </div>
              )}
              {card.oracle_text && (
                <pre className="mt-3 whitespace-pre-wrap font-sans text-sm text-fg-muted leading-relaxed">
                  {card.oracle_text}
                </pre>
              )}
              {card.price_usd != null && (
                <div className="mt-3 font-mono text-[11px] text-fg-subtle tabular-nums">
                  ${card.price_usd.toFixed(2)} USD
                </div>
              )}
              {sc?.scryfall_uri && (
                <a
                  href={sc.scryfall_uri}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-block text-xs text-accent hover:underline"
                >
                  View on Scryfall →
                </a>
              )}
            </div>
          </div>
        )}
      </aside>
    ),
    [card, img, backImg, sc],
  );
}
